import { useEffect } from "react";
import type { TableProps } from "antd";
import { Button, Modal, Space, Table, Typography, Grid } from "antd";
import { Check } from "lucide-react";
import type { ApplicationItem } from "@/types/Application/application";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";
import { AcceptApplicationModal } from "./AcceptApplicationModal";
import { DetailsApplicationModal } from "./DetailsApplicationModal";
import { EditApplicationModal } from "./EditApplicationModal";
import { DeleteApplicationModal } from "./DeleteApplicationModal";
import { RejectApplicationModal } from "./RejectApplicationModal";
import { useVillageList } from "./hook/useVillageList";
import { useApplicationModal } from "./hook/useApplicationModal";
import { formatValue } from "@/utils/formatValue";
import { renderNameCell } from "@/features/admin/hook/renderNameCell";

interface NotApplicationTableProps {
  applications: ApplicationItem[];
  loading: boolean;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  resetKey?: number;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
}

const { Title } = Typography;
const { useBreakpoint } = Grid;

export function NotApplicationTable({
  applications,
  loading,
  totalCount = 0,
  currentPage = 1,
  pageSize = 25,
  resetKey = 0,
  onPageChange,
  onRefresh,
}: NotApplicationTableProps) {
  const { fetchVillageList, villageList } = useVillageList();
    const {
      selectedRow,
      detailData,
      resultType,
  
      isDetailModalOpen,
      isEditModalOpen,
      isDeleteModalOpen,
      isAcceptModalOpen,
      isRejectModalOpen,
      resultModalOpen,

      handleOpenModal,
      handleCloseModal,

      handleOpenEditModal,
      handleCloseEditModal,
      handleOpenDeleteModal,
      handleCloseDeleteModal,
      handleBackFromDeleteModal,
      handleDeleteSuccess,
      handleBackToDetailModal,

      handleOpenAcceptModal,
      handleCloseAcceptModal,
      handleBackFromAcceptModal,
      handleAcceptSuccess,

      handleOpenRejectModal,
      handleCloseRejectModal,
      handleBackFromRejectModal,
      handleRejectSuccess,

      handleCloseResultModal,
    } = useApplicationModal();
  
    const screens = useBreakpoint();
  
    useEffect(() => {
      fetchVillageList();
    }, []);
  

  const columns: TableProps<ApplicationItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "registrationCode",
      sorter: (a, b) =>
        a.registrationCode.localeCompare(b.registrationCode, undefined, {
          numeric: true,
        }),
         render: (value) => formatValue(value),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (value, record) => (
        <span
          className="block cursor-pointer"
          onClick={() => handleOpenModal(record)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenModal(record);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {renderNameCell(value)}
        </span>
      ),
    },
    {
      title: "MyKad/Passport",
      dataIndex: "identityNo",
      sorter: (a, b) => a.identityNo.localeCompare(b.identityNo),
      render: (value) => formatValue(value),
    },
    {
      title: "Jantina",
      dataIndex: "gender",
      filters: [
        { text: "Lelaki", value: "LELAKI" },
        { text: "Perempuan", value: "PEREMPUAN" },
      ],
      onFilter: (value, record) => record.gender === String(value),
    },
    {
      title: "Kategori",
      dataIndex: "residencyStatus",
      sorter: (a, b) => a.residencyStatus.localeCompare(b.residencyStatus),
      render: (value) => formatValue(value),
    },
    {
      title: "Kampung",
      dataIndex: "villageName",
      sorter: (a, b) => a.villageName.localeCompare(b.villageName),
      render: (value) => formatValue(value),
    },
    {
      title: "Tarikh Daftar",
      dataIndex: "submittedOn",
      sorter: (a, b) => {
        const aDate = new Date(a.submittedOn).getTime();
        const bDate = new Date(b.submittedOn).getTime();

        return aDate - bDate;
      },
      render: (value) => safeFormatyyyyMMddDateTime(value),
    },
    {
      title: "Tindakan",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            className="px-4 rounded-lg"
            onClick={() => handleOpenModal(record)}
          >
            Lihat
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
        <Table<ApplicationItem>
          key={resetKey}
          size="middle"
          showHeader
          tableLayout="fixed"
          className="c-table"
          columns={columns}
          dataSource={applications}
          loading={loading}
          rowKey="registrationId"
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            onChange: onPageChange,
            disabled: loading,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `Memaparkan ${range[0]}-${range[1]} daripada ${total}`,
          }}
        />
      </div>

      <DetailsApplicationModal
        open={isDetailModalOpen}
        selectedRow={selectedRow}
        detailData={detailData}
        onClose={handleCloseModal}
        onEdit={handleOpenEditModal}
        onAccept={handleOpenAcceptModal}
        onReject={handleOpenRejectModal}
      />

      <DeleteApplicationModal
        open={isDeleteModalOpen}
        registrationId={selectedRow?.registrationId ?? detailData?.registrationId ?? ""}
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        selectedRow={selectedRow}
        detailData={detailData}
        onClose={handleCloseDeleteModal}
        onBack={handleBackFromDeleteModal}
        onConfirm={handleDeleteSuccess}
        onSuccess={onRefresh}
      />

      <EditApplicationModal
        open={isEditModalOpen}
        application={detailData}
        villageList={villageList ?? []}
        onClose={handleCloseEditModal}
        onBack={handleBackToDetailModal}
        onDelete={handleOpenDeleteModal}
        onSuccess={onRefresh}
      />

      <AcceptApplicationModal
        open={isAcceptModalOpen}
        registrationId={
          selectedRow?.registrationId ?? detailData?.registrationId ?? ""
        }
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        onClose={handleCloseAcceptModal}
        onBack={handleBackFromAcceptModal}
        onConfirm={handleAcceptSuccess}
        onSuccess={onRefresh}
      />

      <RejectApplicationModal
        open={isRejectModalOpen}
        registrationId={
          selectedRow?.registrationId ?? detailData?.registrationId ?? ""
        }
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        onClose={handleCloseRejectModal}
        onBack={handleBackFromRejectModal}
        onConfirm={handleRejectSuccess}
        onSuccess={onRefresh}
      />

      <Modal
        open={resultModalOpen}
        onCancel={handleCloseResultModal}
        footer={false}
        closable={false}
        centered
      >
        <Check
          size={72}
          className="mx-auto mb-4 sm:mb-10 text-[#1F993B]"
          aria-hidden="true"
        />

        <Title
          level={3}
          style={{
            fontSize: screens.xs ? 20 : 24,
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          {resultType === "accepted"
            ? "Permohonan berjaya diterima"
            : resultType === "deleted"
              ? "Permohonan berjaya dipadam"
              : "Permohonan telah ditolak"}
        </Title>
      </Modal>
    </>
  );
}